
import { useState, useEffect } from "react"
import { usePreviewImport } from "../../hooks/useProjectImport"
import type { ProjectImportRow } from "../../hooks/types"

interface PreviewStepProps {
  excelFile: File
  onBack: () => void
  onSuccess: (records: ProjectImportRow[]) => void
  onError: (message: string) => void
}

export default function PreviewStep({ excelFile, onBack, onSuccess, onError }: PreviewStepProps) {
  const [records, setRecords] = useState<ProjectImportRow[]>([])
  const previewImport = usePreviewImport()

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const result = await previewImport.mutateAsync(excelFile)
        setRecords(result.parsedRecords)
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to preview import"
        onError(message)
      }
    }

    fetchPreview()
  }, [excelFile, previewImport, onError])

  const getConflictBadge = (conflicts: string[]) => {
    if (conflicts.length === 0) return null

    return (
      <div className="space-y-1">
        {conflicts.map((conflict, index) => {
          let label = conflict
          let color = "bg-red-100 text-red-800"

          if (conflict === "duplicateCode") {
            label = "Duplicate Code"
          } else if (conflict === "missingClientEmail") {
            label = "Missing Client Email"
          } else if (conflict === "missingClientContact") {
            label = "Client Not Found"
            color = "bg-yellow-100 text-yellow-800"
          }

          return (
            <span
              key={index}
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
            >
              {label}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Preview Import</h3>
        <p className="text-gray-500 mb-4">Review the mapped data and resolve any conflicts before importing.</p>
      </div>

      {previewImport.isPending ? (
        <div className="flex justify-center py-12">
          <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Row
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Conflicts
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record, index) => (
                  <tr key={index} className={record.conflictType.length > 0 ? "bg-red-50" : ""}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.rowIndex}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.mappedRow["Project Code"]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.mappedRow["Title"]}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.mappedRow["Client Email"]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.mappedRow["Status"] || "Awarded"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getConflictBadge(record.conflictType)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between">
            <button type="button" onClick={onBack} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
              Back
            </button>
            <button
              type="button"
              onClick={() => onSuccess(records)}
              disabled={records.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Continue to Import
            </button>
          </div>
        </>
      )}
    </div>
  )
}
